import React, { useState } from 'react';

import { ActionIcon, Box, Button, Flex, Select, Stack, Text, TextInput, Textarea, UnstyledButton } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconX } from '@tabler/icons-react';
import {
  JsonProperty,
  PROPERTY_TYPES
} from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/properties/types';
import { PropertyEditFormData } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/types';
import { ValidationUtils } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/utils';
import { useTranslation } from 'react-i18next';
import { requiredInputValidator, validatePropertyKey } from 'shared/utils/form-validators';

interface ParameterFieldEditorProps {
  mode: 'add' | 'edit';
  path: string[]; // For add: parent path, For edit: full path to property
  getPropertyAtSchemaPath: (path: string[]) => JsonProperty | null;
  getExistingKeys: (parentPath: string[]) => string[];
  isPropertyRequired: (fullPath: string[]) => boolean;
  onSave: (formData: PropertyEditFormData) => void;
  onCancel: () => void;
}

export function ParameterFieldEditor({
  mode,
  path,
  getPropertyAtSchemaPath,
  getExistingKeys,
  isPropertyRequired,
  onSave,
  onCancel
}: ParameterFieldEditorProps) {
  const { t } = useTranslation();

  // For edit mode, derive parent path and key from the full path
  const parentPath = mode === 'edit' ? path.slice(0, -1) : path;
  const editingKey = mode === 'edit' ? path[path.length - 1] : undefined;

  // Get existing property if editing
  const existingProperty = editingKey ? getPropertyAtSchemaPath(path) : null;

  // Initialize form using Mantine's useForm
  const form = useForm<PropertyEditFormData>({
    initialValues: {
      key: (() => {
        if (mode === 'edit' && existingProperty && editingKey) {
          return editingKey === 'items' ? 'item' : editingKey;
        }
        return '';
      })(),
      type: existingProperty?.type || 'string',
      description: existingProperty?.description || '',
      required: mode === 'edit' && editingKey ? isPropertyRequired(path) : true,
      enum: existingProperty?.type === 'string' ? existingProperty.enum || [] : []
    },
    validate: {
      key: (value) => {
        const existingKeys = getExistingKeys(parentPath);
        // Filter out the current key if editing
        const keysForValidation =
          mode === 'edit' && editingKey ? existingKeys.filter((k) => k !== editingKey) : existingKeys;

        return validatePropertyKey(value, keysForValidation);
      },
      description: (value) => requiredInputValidator(value)
    }
  });

  const [enumInputValue, setEnumInputValue] = useState('');

  const handleSave = () => {
    const validation = form.validate();
    if (validation.hasErrors) {
      return;
    }

    try {
      onSave(form.values);
    } catch (error) {
      if (error instanceof Error) {
        form.setFieldError('key', error.message);
      }
    }
  };

  const addEnumValue = () => {
    const trimmedValue = enumInputValue.trim();
    const currentEnumLength = form.values.enum?.length || 0;

    if (trimmedValue && !form.values.enum?.includes(trimmedValue) && currentEnumLength < 20) {
      form.setFieldValue('enum', [...(form.values.enum || []), trimmedValue]);
      setEnumInputValue('');
    }
  };

  const removeEnumValue = (valueToRemove: string) => {
    form.setFieldValue('enum', form.values.enum?.filter((value) => value !== valueToRemove) || []);
  };

  const handleEnumKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEnumValue();
    }
  };

  const toggleRequired = () => {
    form.setFieldValue('required', !form.values.required);
  };

  const canShowEnum = form.values.type === 'string';

  // Get select options with disabled states based on nesting level
  const getSelectOptions = () => {
    return PROPERTY_TYPES.map((type) => {
      if (type === 'object' || type === 'array') {
        const childPath = [...parentPath, 'newProperty', 'childProperty'];
        const canAddChildren = ValidationUtils.validateNestingLevel(childPath);

        return {
          value: type,
          label: type,
          disabled: !canAddChildren
        };
      } else {
        return { value: type, label: type };
      }
    });
  };

  const handleTypeChange = (value: string | null) => {
    if (!value) return;

    const selectedOption = getSelectOptions().find((option) => option.value === value);
    if (selectedOption?.disabled) {
      return;
    }

    form.setFieldValue('type', value as PropertyEditFormData['type']);
    if (value !== 'string') {
      form.setFieldValue('enum', []);
    }
  };

  return (
    <Box
      p="lg"
      sx={(theme) => ({
        border: `1px solid ${theme.other.gray.border}`,
        borderRadius: theme.defaultRadius,
        backgroundColor: theme.colors.gray[0]
      })}>
      <Stack spacing="lg">
        {/* Parameter name row with type and required toggle */}
        <Flex align="flex-start" gap="md">
          <Box style={{ flex: 1 }}>
            <Text size="sm" fw={500} mb="xs" c="dark">
              {t('JSON_SCHEMA_BUILDER.PARAMETER_NAME')}
            </Text>
            <TextInput
              placeholder={
                editingKey === 'items'
                  ? t('JSON_SCHEMA_BUILDER.ARRAY_ITEM_PLACEHOLDER')
                  : t('JSON_SCHEMA_BUILDER.PARAMETER_NAME_PLACEHOLDER')
              }
              disabled={editingKey === 'items'}
              {...form.getInputProps('key')}
              sx={(theme) => ({
                input: {
                  backgroundColor: editingKey === 'items' ? theme.colors.gray[0] : 'white',
                  border: `1px solid ${theme.other.gray.border}`
                }
              })}
            />
          </Box>

          <Box w={120}>
            <Text size="sm" fw={500} mb="xs" c="dark">
              {t('JSON_SCHEMA_BUILDER.TYPE')}
            </Text>
            <Select
              value={form.values.type}
              onChange={handleTypeChange}
              data={getSelectOptions()}
              sx={(theme) => ({
                input: {
                  backgroundColor: 'white',
                  border: `1px solid ${theme.other.gray.border}`
                }
              })}
            />
          </Box>

          <Box>
            <Text size="sm" fw={500} mb="xs" c="dark">
              {t('JSON_SCHEMA_BUILDER.REQUIRED')}
            </Text>
            <UnstyledButton
              onClick={editingKey === 'items' ? undefined : toggleRequired}
              px="xs"
              py={4}
              sx={(theme) => ({
                borderRadius: theme.radius.md,
                border: `1px solid ${theme.other.gray.border}`,
                backgroundColor: form.values.required ? '#fef2f2' : theme.colors.gray[0],
                height: '36px',
                width: '80px',
                textAlign: 'center',
                cursor: editingKey === 'items' ? 'default' : 'pointer',
                opacity: editingKey === 'items' ? 0.7 : 1
              })}>
              <Text size="xs" fw={500} c={form.values.required ? '#dc2626' : 'dimmed'}>
                {form.values.required ? t('JSON_SCHEMA_BUILDER.REQUIRED') : t('JSON_SCHEMA_BUILDER.OPTIONAL')}
              </Text>
            </UnstyledButton>
          </Box>
        </Flex>

        {/* Description */}
        <Box>
          <Text size="sm" fw={500} mb="xs" c="dark">
            {t('JSON_SCHEMA_BUILDER.DESCRIPTION')}
          </Text>
          <Textarea
            placeholder={
              editingKey === 'items'
                ? t('JSON_SCHEMA_BUILDER.ARRAY_ITEM_DESCRIPTION_PLACEHOLDER')
                : t('JSON_SCHEMA_BUILDER.DESCRIPTION_PLACEHOLDER')
            }
            minRows={3}
            autosize
            {...form.getInputProps('description')}
            sx={(theme) => ({
              input: {
                backgroundColor: 'white',
                border: `1px solid ${theme.other.gray.border}`
              }
            })}
          />
        </Box>

        {/* Enum values */}
        {canShowEnum && (
          <Box>
            <Flex align="center" gap="md">
              <Text size="sm" fw={500} c="dark" style={{ minWidth: 'fit-content' }}>
                {t('JSON_SCHEMA_BUILDER.ENUM_VALUES')}
              </Text>
              <TextInput
                placeholder={t('JSON_SCHEMA_BUILDER.ADD_VALUE_PLACEHOLDER', { count: form.values.enum?.length || 0 })}
                value={enumInputValue}
                onChange={(e) => setEnumInputValue(e.target.value)}
                onKeyDown={handleEnumKeyDown}
                style={{ flex: 1 }}
                sx={(theme) => ({
                  input: {
                    backgroundColor: 'white',
                    border: `1px solid ${theme.other.gray.border}`
                  }
                })}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={addEnumValue}
                disabled={!enumInputValue.trim() || (form.values.enum?.length || 0) >= 20}>
                {t('JSON_SCHEMA_BUILDER.ADD')}
              </Button>
            </Flex>

            {form.values.enum && form.values.enum.length > 0 && (
              <Flex gap="xs" wrap="wrap" mt="xs">
                {form.values.enum.map((value, index) => {
                  return (
                    <Box
                      key={index}
                      px="xs"
                      py={2}
                      sx={(theme) => ({
                        backgroundColor: theme.colors.gray[1],
                        color: theme.other.text.dimmed,
                        border: `1px solid ${theme.colors.gray[2]}`,
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 500,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      })}>
                      {value}
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        onClick={() => removeEnumValue(value)}
                        sx={(theme) => ({
                          color: theme.other.text.dimmed,
                          opacity: 0.7,
                          '&:hover': {
                            opacity: 1,
                            backgroundColor: 'rgba(0, 0, 0, 0.1)'
                          }
                        })}>
                        <IconX size={10} />
                      </ActionIcon>
                    </Box>
                  );
                })}
              </Flex>
            )}
          </Box>
        )}

        <Flex gap="sm" justify="flex-end" pt="sm">
          <Button variant="outline" onClick={onCancel}>
            {t('JSON_SCHEMA_BUILDER.CANCEL')}
          </Button>
          <Button variant="filled" onClick={handleSave}>
            {mode === 'add'
              ? t('JSON_SCHEMA_BUILDER.ADD_PARAMETER')
              : editingKey === 'items'
              ? t('JSON_SCHEMA_BUILDER.SAVE_ITEM_CONFIGURATION')
              : t('JSON_SCHEMA_BUILDER.SAVE_CHANGES')}
          </Button>
        </Flex>
      </Stack>
    </Box>
  );
}
